import { useCallback, useRef, useState } from 'react';
import Swal from 'sweetalert2';

export default function useActiveReferralDialogState({
  promoteReferral,
  promoteStatus,
  successText,
  failureText = 'Failed to complete referral!',
}) {
  const [referralUuid, setReferralUuid] = useState('');
  const [referral, setReferral] = useState(null);
  const [status, setStatus] = useState('');
  const [scheduleAppointmentDate, setScheduleAppointmentDate] = useState('');
  const [openNotes, setOpenNotes] = useState(false);
  const [openAppointmentDialog, setOpenAppointmentDialog] = useState(false);
  const [openReferralDialog, setOpenReferralDialog] = useState(false);
  const [openSentDateDialog, setOpenSentDateDialog] = useState(false);
  const [appointmentDate, setAppointmentDate] = useState('');
  const [openCompleteDialog, setOpenCompleteDialog] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const refreshRef = useRef(() => {});

  const setRefresh = useCallback((refresh) => {
    refreshRef.current = refresh;
  }, []);

  const refresh = useCallback(() => {
    refreshRef.current();
  }, []);

  const onPromoteReferral = useCallback(
    (id, data) => {
      setActionLoading(true);
      promoteReferral(id, promoteStatus, data)
        .then(() => {
          const text = typeof successText === 'function' ? successText(data) : successText;
          return Swal.fire({
            icon: 'success',
            text,
            showConfirmButton: true,
          }).then(() => refreshRef.current());
        })
        .catch(() => Swal.fire({ icon: 'error', text: failureText, showConfirmButton: true }))
        .finally(() => setActionLoading(false));
    },
    [promoteReferral, promoteStatus, successText, failureText]
  );

  const handleOnComplete = useCallback(
    (item) => {
      setReferralUuid(item.id);
      setAppointmentDate(item?.appointment?.appointment_date || '');
      if (item.status !== 'declined') {
        setOpenCompleteDialog(true);
      } else {
        Swal.fire({
          title: 'Complete Referral?',
          text: 'Are you sure you want to complete this referral?',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes, complete it',
          cancelButtonText: 'Cancel',
        }).then((result) => {
          if (result.isConfirmed) {
            onPromoteReferral(item.id, 'Referral Completed');
          }
        });
      }
    },
    [onPromoteReferral]
  );

  const openNotesFor = useCallback((id) => {
    setReferralUuid(id);
    setOpenNotes(true);
  }, []);

  const openReferralEditor = useCallback((selectedReferral) => {
    setReferral(selectedReferral);
    setOpenReferralDialog(true);
  }, []);

  const openSentDateEditor = useCallback((selectedReferral) => {
    setReferral(selectedReferral);
    setOpenSentDateDialog(true);
  }, []);

  const openAppointmentFor = useCallback(({ id, appointment, nextStatus }) => {
    setScheduleAppointmentDate(appointment?.appointment_date);
    setReferralUuid(id);
    setStatus(nextStatus);
    setOpenAppointmentDialog(true);
  }, []);

  const closeAndRefreshAppointmentDialog = useCallback(() => {
    setOpenAppointmentDialog(false);
    refreshRef.current();
  }, []);

  const closeAndRefreshReferralDialog = useCallback(() => {
    setOpenReferralDialog(false);
    refreshRef.current();
  }, []);

  const closeAndRefreshSentDateDialog = useCallback(() => {
    setOpenSentDateDialog(false);
    refreshRef.current();
  }, []);

  return {
    referralUuid,
    referral,
    status,
    scheduleAppointmentDate,
    openNotes,
    openAppointmentDialog,
    openReferralDialog,
    openSentDateDialog,
    appointmentDate,
    openCompleteDialog,
    actionLoading,
    setActionLoading,
    setOpenNotes,
    setOpenCompleteDialog,
    setRefresh,
    refresh,
    onPromoteReferral,
    handleOnComplete,
    openNotesFor,
    openReferralEditor,
    openSentDateEditor,
    openAppointmentFor,
    closeAndRefreshAppointmentDialog,
    closeAndRefreshReferralDialog,
    closeAndRefreshSentDateDialog,
  };
}
